import { z } from 'zod';
import { baseDocSchema, localeSchema, objectIdSchema, priceCentsSchema } from './common.js';

export const ticketKindSchema = z.enum(['entry', 'combo', 'seat', 'consumption']);
export type TicketKind = z.infer<typeof ticketKindSchema>;

export const ticketTypeTranslationSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(2000).default(''),
});
export type TicketTypeTranslation = z.infer<typeof ticketTypeTranslationSchema>;

/**
 * Límite por compra que fija el promoter (ej. "máx 2 por compra").
 * `null` = sin límite. Aplica por order, no por buyer histórico.
 */
export const maxPerOrderSchema = z.number().int().min(1).max(100);

export const ticketTypeSchema = baseDocSchema.extend({
  eventId: objectIdSchema,
  orgId: objectIdSchema,
  kind: ticketKindSchema,
  priceCents: priceCentsSchema,
  capacity: z.number().int().nonnegative(),
  sold: z.number().int().nonnegative().default(0),
  maxPerOrder: maxPerOrderSchema.nullable().default(null),
  hidden: z.boolean().default(false),
  validityFrom: z.coerce.date().optional(),
  validityTo: z.coerce.date().optional(),
  comboItems: z.array(objectIdSchema).default([]),
  translations: z.record(localeSchema, ticketTypeTranslationSchema),
  sortOrder: z.number().int().default(0),
});
export type TicketType = z.infer<typeof ticketTypeSchema>;

export const createTicketTypeInputSchema = z.object({
  kind: ticketKindSchema,
  priceCents: priceCentsSchema,
  capacity: z.number().int().nonnegative(),
  maxPerOrder: maxPerOrderSchema.nullable().default(null),
  hidden: z.boolean().default(false),
  validityFrom: z.coerce.date().optional(),
  validityTo: z.coerce.date().optional(),
  comboItems: z.array(objectIdSchema).default([]),
  translations: z.record(localeSchema, ticketTypeTranslationSchema),
});
export type CreateTicketTypeInput = z.infer<typeof createTicketTypeInputSchema>;

/**
 * Patch parcial para ticket types existentes. Todos los campos son
 * opcionales, pero hay que mandar al menos uno. `capacity` no puede
 * bajar por debajo de `sold` (validación en el handler — no acá porque
 * acá no conocemos `sold`). `kind` y `comboItems` no son editables
 * porque cambiar el tipo después de venta rompe los QRs ya emitidos.
 */
export const updateTicketTypeInputSchema = z
  .object({
    priceCents: priceCentsSchema.optional(),
    capacity: z.number().int().nonnegative().optional(),
    maxPerOrder: maxPerOrderSchema.nullable().optional(),
    hidden: z.boolean().optional(),
    validityFrom: z.coerce.date().nullable().optional(),
    validityTo: z.coerce.date().nullable().optional(),
    sortOrder: z.number().int().min(0).max(9999).optional(),
    translations: z.record(localeSchema, ticketTypeTranslationSchema).optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'Provide at least one field to update',
  });
export type UpdateTicketTypeInput = z.infer<typeof updateTicketTypeInputSchema>;
